import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, User, Phone, Building2, MessageSquare } from "lucide-react";
import { toast } from "sonner";

const inputCls =
  "w-full h-12 px-4 rounded-xl bg-foreground/[0.03] dark:bg-white/[0.04] border border-border focus:border-[#FF7A00] focus:ring-2 focus:ring-[#FF7A00]/30 outline-none transition-all text-sm md:text-base placeholder:text-muted-foreground/70";

const Field = ({ id, label, icon: Icon, children }) => (
  <label htmlFor={id} className="block group">
    <div className="text-xs font-semibold tracking-[0.18em] uppercase text-muted-foreground mb-2 flex items-center gap-2">
      <Icon className="h-3.5 w-3.5 text-[#FF9933]" />
      {label}
    </div>
    {children}
  </label>
);

export const EnquiryForm = () => {
  const [form, setForm] = useState({
    name: "",
    mobile: "",
    business: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = (e) => {
    e.preventDefault();
    toast.dismiss();
    if (!form.name.trim() || !form.mobile.trim()) {
      toast.error("Please share at least your name and mobile number.");
      return;
    }
    if (!/^\d{7,15}$/.test(form.mobile.replace(/\D/g, ""))) {
      toast.error("Please enter a valid mobile number.");
      return;
    }
    setSubmitting(true);
    // Frontend-only — no backend submission.
    // eslint-disable-next-line no-console
    console.log("[DIGINDIA] Enquiry submitted:", form);
    setTimeout(() => {
      setSubmitting(false);
      toast.success(
        `Thank you, ${form.name.split(" ")[0]}! Our team will reach you within 2 hours.`,
        { description: "Your 48-hour build clock starts now." }
      );
      setForm({ name: "", mobile: "", business: "", message: "" });
    }, 700);
  };

  return (
    <section
      id="enquiry"
      className="relative py-24 md:py-32 overflow-hidden"
      data-testid="enquiry-section"
    >
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
      <div className="absolute -left-32 top-1/4 h-96 w-96 rounded-full bg-[#FF7A00]/10 blur-[140px]" />
      <div className="absolute -right-32 bottom-1/4 h-96 w-96 rounded-full bg-[#00C853]/10 blur-[140px]" />

      <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12">
        {/* Left copy */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-5 space-y-6"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-border text-xs font-semibold tracking-[0.2em] uppercase text-muted-foreground">
            <span className="h-1.5 w-1.5 rounded-full bg-[#FF7A00]" />
            Start your build
          </div>
          <h2
            className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-[1.05]"
            data-testid="enquiry-heading"
          >
            Tell us about your{" "}
            <span className="text-gradient-saffron">business.</span>
            <br />
            We&apos;ll be live in{" "}
            <span className="text-gradient-green">48 hours.</span>
          </h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-md">
            One short form. A real human calls back within 2 hours with a free
            digital identity blueprint for your business.
          </p>

          <div className="space-y-3 pt-2">
            {[
              "Free digital identity blueprint",
              "Fixed-price 48-hour delivery",
              "Hindi & regional language support",
            ].map((t) => (
              <div key={t} className="flex items-center gap-3 text-sm">
                <span className="h-6 w-6 rounded-full gradient-saffron grid place-items-center text-white text-[11px] font-bold">
                  ✓
                </span>
                <span className="font-medium">{t}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Form */}
        <motion.form
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          onSubmit={onSubmit}
          className="lg:col-span-7 dark:glass-dark glass-light rounded-3xl p-6 md:p-10 border border-border space-y-5"
          data-testid="enquiry-form"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <Field id="name" label="Your name" icon={User}>
              <input
                id="name"
                type="text"
                value={form.name}
                onChange={update("name")}
                placeholder="Aarav Sharma"
                className={inputCls}
                required
                data-testid="form-input-name"
              />
            </Field>
            <Field id="mobile" label="Mobile" icon={Phone}>
              <input
                id="mobile"
                type="tel"
                value={form.mobile}
                onChange={update("mobile")}
                placeholder="+91 98xxx xxxxx"
                className={inputCls}
                required
                data-testid="form-input-mobile"
              />
            </Field>
          </div>
          <Field id="business" label="Business" icon={Building2}>
            <input
              id="business"
              type="text"
              value={form.business}
              onChange={update("business")}
              placeholder="Saree boutique, kirana, clinic, etc."
              className={inputCls}
              data-testid="form-input-business"
            />
          </Field>
          <Field id="message" label="What do you need?" icon={MessageSquare}>
            <textarea
              id="message"
              value={form.message}
              onChange={update("message")}
              placeholder="Tell us a bit about your goals…"
              rows={4}
              className={`${inputCls} h-auto py-3 resize-none`}
              data-testid="form-input-message"
            />
          </Field>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-2">
            <p className="text-xs text-muted-foreground">
              By submitting, you agree to be contacted by DIGINDIA PAYTECH.
            </p>
            <button
              type="submit"
              disabled={submitting}
              className="group inline-flex items-center justify-center gap-3 h-13 py-3.5 px-6 rounded-full gradient-saffron text-white font-semibold text-sm shadow-[0_12px_40px_rgba(255,122,0,0.35)] hover:shadow-[0_18px_60px_rgba(255,122,0,0.5)] hover:-translate-y-0.5 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="form-submit-button"
            >
              {submitting ? "Sending…" : "Submit enquiry"}
              <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
            </button>
          </div>
        </motion.form>
      </div>
    </section>
  );
};

export default EnquiryForm;
